import React, { useEffect, useRef } from "react";
import { Check, ChevronLeft, PenLine } from "lucide-react";

import { BrandButton } from "@/shared/components/ui-kit";
import { cn } from "@/shared/lib/cn";

import { answered, answerText, isRecommended, plainLabel, type AskQuestion } from "../askUser";
import type { QuestionPicks } from "../hooks/useQuestionPicks";
import type { PendingReply } from "../types";

/* A run that asked the person something (`ask_user`) and waits for the answers: one question at
   a time, ui.definable.ai's Stepper Form pane with its options as Model Picker rows. A single
   choice answers its question and the next one slides in; "Something else" opens a field for
   their own words. The last question sends every answer as words. Rendered in ComposerDock's
   Questions tab, which shows the questions' steps on its strip (QuestionSteps). */
export default function PendingQuestion({ questions, picks, onAnswer, onStop, busy = false }: {
  /** What the run asks; a question with no options takes words alone. */
  questions: AskQuestion[];
  picks: QuestionPicks;
  onAnswer: (reply: PendingReply) => void;
  /** Ends the run instead of answering it. */
  onStop?: () => void;
  /** The answers are on their way. */
  busy?: boolean;
}) {
  const { page, back } = picks;
  const question = questions[page];
  const pick = picks.picks[page];
  const last = page === questions.length - 1;
  const ready = last ? picks.picks.every(answered) : answered(pick);
  const multi = !!question.multi_select;

  const next = () => {
    if (!ready || busy) return;
    if (last) onAnswer({ text: answerText(questions, picks.picks) });
    else picks.go(page + 1);
  };
  // the number keys pick, while the field isn't taking words and no shortcut is meant
  const onKeyDown = (e: React.KeyboardEvent) => {
    const option = Number(e.key) - 1;
    if (busy || e.metaKey || e.ctrlKey || e.altKey || e.target instanceof HTMLInputElement) return;
    if (!(option >= 0 && option < question.options.length)) return;
    e.preventDefault();
    picks.choose(option);
  };

  // the pane is drawn anew for each question: the keys follow it to its picked or first row
  const pane = useRef<HTMLDivElement>(null);
  const shown = useRef(page);
  useEffect(() => {
    if (shown.current === page) return;
    shown.current = page;
    const row = pane.current?.querySelector<HTMLElement>("[aria-checked=true]") ?? pane.current?.querySelector<HTMLElement>("[role=radio], [role=checkbox]");
    row?.focus({ preventScroll: true });
  }, [page]);

  return (
    <div role="group" aria-label="Questions" aria-busy={busy} onKeyDown={onKeyDown}>
      <div key={page} ref={pane} className={cn("px-5 pt-[18px] pb-1", back ? "step-in-back" : page > 0 && "step-in")}>
        <h3 className="m-0 px-1 text-[15px] font-medium leading-[22px] tracking-[-0.005em] text-[var(--text-primary)] [overflow-wrap:anywhere] whitespace-pre-wrap">
          {question.question}
        </h3>
        {question.options.length > 0 ? (
          <>
            <p className="m-0 mt-0.5 px-1 text-[13px] text-[var(--text-tertiary)]">{multi ? "Pick any that apply." : "Pick one."}</p>
            <div role={multi ? "group" : "radiogroup"} aria-label={question.question} className="mt-3 -mx-2 grid grid-cols-1 gap-0.5">
              {question.options.map((option, i) => (
                <OptionRow
                  key={option.label}
                  role={multi ? "checkbox" : "radio"}
                  checked={pick.options.includes(i)}
                  mark={i + 1}
                  title={plainLabel(option.label)}
                  recommended={isRecommended(option.label)}
                  description={option.description}
                  onClick={() => picks.choose(i)}
                  disabled={busy}
                />
              ))}
              <OwnWords role={multi ? "checkbox" : "radio"} own={pick.own} onChoose={picks.chooseOwn} onWrite={picks.writeOwn} onEnter={next} disabled={busy} />
            </div>
          </>
        ) : (
          <OwnField value={pick.own ?? ""} open onWrite={picks.writeOwn} onEnter={next} disabled={busy} placeholder="Type your answer…" />
        )}
      </div>
      <div className="flex items-center gap-2 px-4 pt-3.5 pb-4">
        <button
          type="button"
          onClick={() => picks.go(page - 1)}
          disabled={busy}
          className={cn("press h-8 pl-1.5 pr-2.5 rounded-[10px] inline-flex items-center gap-1 text-[13px] font-medium text-[var(--text-secondary)] hover:bg-[var(--accent-hover)] hover:text-[var(--text-primary)]", page === 0 && "invisible")}
        >
          <ChevronLeft className="w-3.5 h-3.5" strokeWidth={2} /> Back
        </button>
        {onStop && (
          <button type="button" onClick={onStop} disabled={busy} className="press ml-auto h-8 px-2.5 rounded-[10px] text-[12.5px] text-[var(--text-tertiary)] hover:bg-[var(--accent-hover)] hover:text-[var(--text-primary)] disabled:opacity-40">
            Stop run
          </button>
        )}
        <BrandButton type="button" onClick={next} disabled={!ready || busy} className={cn("disabled:opacity-35", !onStop && "ml-auto")}>
          {busy ? "Sending…" : !last ? "Next" : questions.length > 1 ? "Send answers" : "Send"}
        </BrandButton>
      </div>
    </div>
  );
}

const ROW = "w-full flex items-start gap-3 px-3 py-[9px] rounded-[10px] text-left transition-colors duration-150";

/** One option: its number (a check once picked), its label and what it means. */
function OptionRow({ role, checked, mark, title, recommended, description, onClick, disabled }: {
  role: "radio" | "checkbox";
  checked: boolean;
  mark: number;
  title: string;
  recommended: boolean;
  description: string;
  onClick: () => void;
  disabled: boolean;
}) {
  return (
    <button
      type="button"
      role={role}
      aria-checked={checked}
      onClick={onClick}
      disabled={disabled}
      className={cn(ROW, "group hover:bg-[var(--accent-hover)] aria-checked:bg-[var(--accent-active)]")}
    >
      <Tile checked={checked}>{mark}</Tile>
      <span className="min-w-0 flex-1 grid grid-cols-1 gap-px">
        <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 min-w-0 text-[13.5px] font-medium leading-5 text-[var(--text-primary)] [overflow-wrap:anywhere]">
          {title}
          {recommended && (
            <span className="px-1.5 rounded-[6px] bg-[var(--surface-2)] group-aria-checked:bg-[var(--surface)] text-[11px] font-medium leading-[18px] text-[var(--text-secondary)]">
              Recommended
            </span>
          )}
        </span>
        {description && <span className="text-[12.5px] leading-[18px] text-[var(--text-tertiary)] [overflow-wrap:anywhere]">{description}</span>}
      </span>
    </button>
  );
}

/** "Something else": the last row, which opens a field for the person's own words. */
function OwnWords({ role, own, onChoose, onWrite, onEnter, disabled }: {
  role: "radio" | "checkbox";
  /** Their words; null while the row isn't chosen. */
  own: string | null;
  onChoose: () => void;
  onWrite: (words: string) => void;
  onEnter: () => void;
  disabled: boolean;
}) {
  const open = own !== null;
  return (
    <div className={cn("rounded-[10px] transition-colors duration-150", open ? "bg-[var(--accent-active)]" : "hover:bg-[var(--accent-hover)]")}>
      <button type="button" role={role} aria-checked={open} onClick={onChoose} disabled={disabled} className={cn(ROW, "items-center")}>
        <Tile checked={open}><PenLine className="w-3 h-3" strokeWidth={2} /></Tile>
        <span className={cn("text-[13.5px] leading-5", open ? "font-medium text-[var(--text-primary)]" : "text-[var(--text-secondary)]")}>Something else</span>
      </button>
      <OwnField value={own ?? ""} open={open} onWrite={onWrite} onEnter={onEnter} disabled={disabled} placeholder="Say it in your own words" inset />
    </div>
  );
}

/** The field the person's own words go in. Folding open, it grows the card over 260ms rather
    than jumping, and takes the cursor without scrolling the chat. */
function OwnField({ value, open, onWrite, onEnter, disabled, placeholder, inset = false }: {
  value: string;
  open: boolean;
  onWrite: (words: string) => void;
  onEnter: () => void;
  disabled: boolean;
  placeholder: string;
  /** Under the "Something else" row, lined up with its words. */
  inset?: boolean;
}) {
  const field = useRef<HTMLInputElement>(null);
  const wasOpen = useRef(open);
  useEffect(() => {
    if (open && !wasOpen.current) field.current?.focus({ preventScroll: true });
    wasOpen.current = open;
  }, [open]);
  return (
    <div className={cn("grid transition-[grid-template-rows] [transition-duration:260ms] [transition-timing-function:var(--easing)]", open ? "grid-rows-[1fr]" : "grid-rows-[0fr]")}>
      <div className={cn("min-h-0 overflow-hidden", inset ? "pl-12 pr-3" : "mt-3")}>
        <input
          ref={field}
          value={value}
          onChange={(e) => onWrite(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !(e.nativeEvent.isComposing || e.keyCode === 229)) {
              e.preventDefault();
              onEnter();
            }
          }}
          tabIndex={open ? 0 : -1}
          disabled={disabled}
          aria-label="Your own answer"
          placeholder={placeholder}
          className={cn(
            "block w-full h-[34px] px-2.5 rounded-[8px] bg-[var(--surface)] ring-1 ring-[var(--hairline-strong)] focus:ring-[var(--hairline-focus)] shadow-lift outline-none text-[16px] sm:text-[13.5px] text-[var(--text-primary)] placeholder:text-[var(--text-quaternary)]",
            inset && "mb-3",
          )}
        />
      </div>
    </div>
  );
}

/** The row's mark: a number on a white tile, turning ink with a check once picked. */
function Tile({ checked, children }: { checked: boolean; children: React.ReactNode }) {
  return (
    <span
      aria-hidden
      className={cn(
        "mt-px w-6 h-6 shrink-0 rounded-[7px] grid place-items-center text-[12px] font-medium tabular-nums transition-colors duration-200",
        checked ? "bg-[var(--brand)] text-[var(--brand-foreground)] shadow-button" : "bg-[var(--surface)] text-[var(--text-tertiary)] ring-1 ring-[var(--hairline-strong)] shadow-lift",
      )}
    >
      {checked ? <Check className="pin-pop w-3 h-3" strokeWidth={2.8} /> : children}
    </span>
  );
}
